import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import { COUNTRIES } from "~/lib/countries";
import {
    IOC_TYPES,
    productSupportsDetectionScope,
    useCreateLicense,
} from "~/lib/licenses";
import { productSupportsIoC, useProducts } from "~/lib/products";
import { useTenants } from "~/lib/tenants";
import { useUsers } from "~/lib/users";

import { DetectionScopesMatrix } from "./DetectionScopesMatrix";

// Country-select sentinel for the "Other…" fallback path.
const OTHER_SENTINEL = "__other__";

const modalStyle: React.CSSProperties = { padding: "var(--space-6)" };

interface CreateLicenseDialogProps {
    /** When supplied, the tenant picker is hidden and this id is used. */
    initialTenantId?: string;
}

export function CreateLicenseDialog({ initialTenantId }: CreateLicenseDialogProps): React.ReactElement {
    const [open, setOpen] = useState(false);
    const [tenantId, setTenantId] = useState<string>(initialTenantId ?? "");
    const [productSlug, setProductSlug] = useState<string>("");
    const [name, setName] = useState("");
    const [iocTypes, setIocTypes] = useState<string[]>([]);
    const [detectionScopes, setDetectionScopes] = useState<Record<string, string[]>>({});
    const [quotaTotal, setQuotaTotal] = useState<string>("");
    const [quotaPerDay, setQuotaPerDay] = useState<string>("");
    const [seats, setSeats] = useState<string>("");
    const [devices, setDevices] = useState<string>("");
    const [expiresAt, setExpiresAt] = useState<string>("");
    const [validFrom, setValidFrom] = useState<string>("");
    const [countrySelect, setCountrySelect] = useState<string>("");
    const [countryOther, setCountryOther] = useState<string>("");
    const [notes, setNotes] = useState<string>("");
    const [primaryContactUserId, setPrimaryContactUserId] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const tenants = useTenants();
    const products = useProducts();
    const users = useUsers();
    const create = useCreateLicense();

    const tenantUsers = useMemo(
        () => (tenantId ? users.data?.filter((u) => u.tenantId === tenantId) ?? [] : []),
        [users.data, tenantId],
    );

    const product = useMemo(
        () => products.data?.find((p) => p.slug === productSlug),
        [products.data, productSlug],
    );

    // When the product changes, drop any previously-selected IoC types that
    // the new product doesn't cover so the chip state stays consistent.
    useEffect(() => {
        if (!product) return;
        setIocTypes((cur) => cur.filter((slug) => productSupportsIoC(product.capabilities, slug)));
        setDetectionScopes((cur) => {
            const next: Record<string, string[]> = {};
            for (const [ruleType, ctxs] of Object.entries(cur)) {
                const filtered = ctxs.filter((c) =>
                    productSupportsDetectionScope(product.capabilities, ruleType, c),
                );
                if (filtered.length > 0) next[ruleType] = filtered;
            }
            return next;
        });
    }, [product]);

    function reset(): void {
        setTenantId(initialTenantId ?? "");
        setProductSlug("");
        setName("");
        setIocTypes([]);
        setDetectionScopes({});
        setQuotaTotal("");
        setQuotaPerDay("");
        setSeats("");
        setDevices("");
        setExpiresAt("");
        setValidFrom("");
        setCountrySelect("");
        setCountryOther("");
        setNotes("");
        setPrimaryContactUserId("");
        setError(null);
        create.reset();
    }

    function toggleIoCType(slug: string): void {
        setIocTypes((cur) => (cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]));
    }

    function parseInt64(v: string): number | undefined {
        if (v.trim() === "") return undefined;
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return NaN as unknown as number;
        return n;
    }

    async function onSubmit(e: React.FormEvent): Promise<void> {
        e.preventDefault();
        setError(null);
        if (!tenantId) {
            setError("Tenant required.");
            return;
        }
        if (!productSlug) {
            setError("Product required.");
            return;
        }
        if (!name.trim()) {
            setError("Name required.");
            return;
        }
        const qt = parseInt64(quotaTotal);
        const qd = parseInt64(quotaPerDay);
        const sv = parseInt64(seats);
        const dv = parseInt64(devices);
        if ([qt, qd, sv, dv].some((n) => Number.isNaN(n))) {
            setError("Numeric fields must be non-negative integers.");
            return;
        }
        let expiresIso: string | undefined;
        if (expiresAt) {
            const d = new Date(expiresAt);
            if (Number.isNaN(d.getTime())) {
                setError("Expiry must be a valid date.");
                return;
            }
            expiresIso = d.toISOString();
        }
        let validFromIso: string | undefined;
        if (validFrom) {
            const d = new Date(validFrom);
            if (Number.isNaN(d.getTime())) {
                setError("Valid-from must be a valid date.");
                return;
            }
            validFromIso = d.toISOString();
        }
        if (validFromIso && expiresIso && validFromIso > expiresIso) {
            setError("Valid-from must be on or before the expiry date.");
            return;
        }
        let country = "";
        if (countrySelect === OTHER_SENTINEL) {
            country = countryOther.trim().toUpperCase();
            if (country && country.length !== 2) {
                setError("Country must be an ISO 3166-1 alpha-2 code (2 letters).");
                return;
            }
        } else {
            country = countrySelect;
        }
        try {
            const res = await create.mutateAsync({
                tenantId,
                productSlug,
                name: name.trim(),
                iocTypes,
                detectionScopes,
                quotaTotal: qt,
                quotaPerDay: qd,
                seats: sv,
                devices: dv,
                expiresAt: expiresIso,
                validFrom: validFromIso,
                country: country || undefined,
                notes: notes.trim() || undefined,
                primaryContactUserId: primaryContactUserId || undefined,
            });
            toast.success(`License “${res.license.name}” created`);
            setOpen(false);
            reset();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to create license.");
        }
    }

    return (
        <Dialog.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) reset();
            }}
        >
            <Dialog.Trigger asChild>
                <button type="button" className="btn btn--primary press-crush">
                    <Plus size={14} />
                    <span>New license</span>
                </button>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="dialog-overlay">
                <Dialog.Content className="dialog-content dialog-content--md enter-up" style={modalStyle}>
                    <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                        <Dialog.Title className="type-h2">New license</Dialog.Title>
                        <Dialog.Close asChild>
                            <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                <X size={14} />
                            </button>
                        </Dialog.Close>
                    </div>
                    <Dialog.Description className="text-text-muted text-12" style={{ marginTop: "var(--space-2)" }}>
                        A license grants a tenant access to one product. API keys (issued from the license detail
                        view) carry the credentials used by clients.
                    </Dialog.Description>

                    <form onSubmit={onSubmit} className="stack" style={{ marginTop: "var(--space-4)" }}>
                        {!initialTenantId && (
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Tenant</span>
                                <select
                                    className="input"
                                    value={tenantId}
                                    onChange={(e) => setTenantId(e.target.value)}
                                    required
                                >
                                    <option value="">— select tenant —</option>
                                    {tenants.data?.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name} ({t.slug})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}

                        <label className="stack-xs">
                            <span className="text-12 text-text-muted">Product</span>
                            <select
                                className="input"
                                value={productSlug}
                                onChange={(e) => setProductSlug(e.target.value)}
                                required
                            >
                                <option value="">— select product —</option>
                                {products.data?.map((p) => (
                                    <option key={p.slug} value={p.slug}>
                                        {p.name} ({p.model})
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="stack-xs">
                            <span className="text-12 text-text-muted">Name</span>
                            <input
                                className="input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={product ? `${product.name} — production` : "Production license"}
                                required
                            />
                        </label>

                        <div className="stack-xs">
                            <span className="text-12 text-text-muted">
                                IoC types <span className="text-text-subtle">(empty = all granted)</span>
                            </span>
                            <div className="cluster-xs">
                                {IOC_TYPES.map((t) => {
                                    const active = iocTypes.includes(t.slug);
                                    const supported = product
                                        ? productSupportsIoC(product.capabilities, t.slug)
                                        : true;
                                    const disabled = !!product && !supported;
                                    return (
                                        <button
                                            key={t.slug}
                                            type="button"
                                            onClick={() => {
                                                if (disabled) return;
                                                toggleIoCType(t.slug);
                                            }}
                                            className={`chip ${active ? "chip--accent" : ""}`}
                                            style={{
                                                cursor: disabled ? "not-allowed" : "pointer",
                                                height: 24,
                                                opacity: disabled ? 0.35 : active ? 1 : 0.6,
                                            }}
                                            aria-pressed={active}
                                            aria-disabled={disabled}
                                            title={
                                                disabled
                                                    ? `${product?.name ?? "This product"} has no capability that covers ${t.label}`
                                                    : undefined
                                            }
                                        >
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {product && (
                                <span className="text-12 text-text-subtle">
                                    Disabled types are not covered by any capability on{" "}
                                    <code className="type-mono-sm">{product.slug}</code>.
                                </span>
                            )}
                        </div>

                        <div className="stack-xs">
                            <span className="text-12 text-text-muted">
                                Detection rule access{" "}
                                <span className="text-text-subtle">
                                    (empty = no detection access)
                                </span>
                            </span>
                            <DetectionScopesMatrix
                                value={detectionScopes}
                                onChange={setDetectionScopes}
                                productCapabilities={product?.capabilities}
                            />
                        </div>

                        {product?.model === "query" && (
                            <div className="cluster" style={{ gap: "var(--space-3)" }}>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">
                                        Quota / day <span className="text-text-subtle">(empty = unlimited)</span>
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        className="input"
                                        value={quotaPerDay}
                                        onChange={(e) => setQuotaPerDay(e.target.value)}
                                        placeholder="100000"
                                    />
                                </label>
                                <label className="stack-xs" style={{ flex: 1 }}>
                                    <span className="text-12 text-text-muted">
                                        Quota total <span className="text-text-subtle">(empty = unlimited)</span>
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        className="input"
                                        value={quotaTotal}
                                        onChange={(e) => setQuotaTotal(e.target.value)}
                                        placeholder="10000000"
                                    />
                                </label>
                            </div>
                        )}
                        {product?.model === "seat" && (
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Seats</span>
                                <input
                                    type="number"
                                    min={0}
                                    className="input"
                                    value={seats}
                                    onChange={(e) => setSeats(e.target.value)}
                                    placeholder="50"
                                />
                            </label>
                        )}
                        {product?.model === "device" && (
                            <label className="stack-xs">
                                <span className="text-12 text-text-muted">Devices</span>
                                <input
                                    type="number"
                                    min={0}
                                    className="input"
                                    value={devices}
                                    onChange={(e) => setDevices(e.target.value)}
                                    placeholder="200"
                                />
                            </label>
                        )}

                        <div className="cluster" style={{ gap: "var(--space-3)" }}>
                            <label className="stack-xs" style={{ flex: 1 }}>
                                <span className="text-12 text-text-muted">
                                    Valid from <span className="text-text-subtle">(optional)</span>
                                </span>
                                <input
                                    type="datetime-local"
                                    className="input"
                                    value={validFrom}
                                    onChange={(e) => setValidFrom(e.target.value)}
                                />
                            </label>
                            <label className="stack-xs" style={{ flex: 1 }}>
                                <span className="text-12 text-text-muted">
                                    Expires at <span className="text-text-subtle">(optional)</span>
                                </span>
                                <input
                                    type="datetime-local"
                                    className="input"
                                    value={expiresAt}
                                    onChange={(e) => setExpiresAt(e.target.value)}
                                />
                            </label>
                        </div>

                        <label className="stack-xs">
                            <span className="text-12 text-text-muted">
                                Country <span className="text-text-subtle">(jurisdiction / export control)</span>
                            </span>
                            <select
                                className="input"
                                value={countrySelect}
                                onChange={(e) => setCountrySelect(e.target.value)}
                            >
                                <option value="">—</option>
                                {COUNTRIES.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.flag} {c.name} ({c.code})
                                    </option>
                                ))}
                                <option value={OTHER_SENTINEL}>Other…</option>
                            </select>
                            {countrySelect === OTHER_SENTINEL && (
                                <input
                                    className="input"
                                    value={countryOther}
                                    onChange={(e) => setCountryOther(e.target.value)}
                                    placeholder="alpha-2 code"
                                    maxLength={2}
                                    style={{
                                        marginTop: "var(--space-1)",
                                        textTransform: "uppercase",
                                    }}
                                />
                            )}
                        </label>

                        <label className="stack-xs">
                            <span className="text-12 text-text-muted">
                                Primary contact <span className="text-text-subtle">(user at this tenant)</span>
                            </span>
                            <select
                                className="input"
                                value={primaryContactUserId}
                                onChange={(e) => setPrimaryContactUserId(e.target.value)}
                                disabled={!tenantId}
                            >
                                <option value="">— none —</option>
                                {tenantUsers.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.email}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="stack-xs">
                            <span className="text-12 text-text-muted">
                                Notes <span className="text-text-subtle">(contractual coverage, optional)</span>
                            </span>
                            <textarea
                                className="input"
                                rows={2}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </label>

                        {error && (
                            <div
                                className="chip chip--danger shake"
                                style={{ height: "auto", padding: "var(--space-2) var(--space-3)" }}
                                role="alert"
                            >
                                {error}
                            </div>
                        )}

                        <button type="submit" className="btn btn--primary press-crush" disabled={create.isPending}>
                            {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            <span>{create.isPending ? "Creating…" : "Create license"}</span>
                        </button>
                    </form>
                </Dialog.Content>
                </Dialog.Overlay>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
